import React, { useState, useEffect, useContext, useRef } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { AuthContext } from "../App";
import { getUserRole } from "./utilities/getUserRole";
import { FiUser, FiSettings, FiLogOut, FiChevronDown, FiLock, FiRefreshCw, FiMenu, FiX } from "react-icons/fi";
import { MdAdminPanelSettings } from "react-icons/md";
import { IoMdLock } from "react-icons/io";
import { BsUnlockFill } from "react-icons/bs";
import axios from "axios";
import { useToast } from '@chakra-ui/react';
import { useSidebar } from "./Layout";

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toggle: toggleSidebar } = useSidebar();
  const { logout } = useContext(AuthContext) || {};
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const toast = useToast();

  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
  });
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const user = getUserRole();
  const userRole = user?.role || "admin";
  const userName = user?.fullName || user?.email || "User";
  const userEmail = user?.email || "";

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setUserDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const onEsc = (e) => {
      if (e.key === "Escape") {
        setShowPasswordModal(false);
        setUserDropdownOpen(false);
      }
    };
    document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }, []);

  const handleLogout = () => {
    if (logout) logout();
    navigate("/login");
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    try {
      if (passwordData.newPassword !== confirmPassword) {
        toast({
          title: "New Password and Confirm Password do not match!",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        return;
      }
      await axios.put(
        `/api/v1/adminManagment/admins-change-password`,
        passwordData
      );
      toast({
        title: "Password updated successfully!",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      setShowPasswordModal(false);
      setPasswordData({ currentPassword: "", newPassword: "" });
      setConfirmPassword("");
    } catch (error) {
      console.error(error);
      const message = error.response?.data?.message || "Error updating password";
      toast({
        title: message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const closePasswordModal = () => {
    setShowPasswordModal(false);
    setPasswordData({ currentPassword: "", newPassword: "" });
    setConfirmPassword("");
  };

  const isSuperAdmin = userRole === "super-admin";

  return (
    <header
      className="fixed top-0 right-0 h-16 z-40 flex items-center justify-between px-4 sm:px-6 header-full lg:left-72"
      style={{
        background: "linear-gradient(180deg, rgba(7, 21, 56, 0.88) 0%, rgba(3, 11, 30, 0.94) 100%)",
        backdropFilter: "blur(18px)",
        WebkitBackdropFilter: "blur(18px)",
        borderBottom: "1px solid rgba(56, 189, 248, 0.2)",
        boxShadow: "0 4px 30px rgba(2, 6, 23, 0.65)",
      }}
    >
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <button
          onClick={toggleSidebar}
          className="lg:hidden text-cyan-200 hover:text-white p-2 rounded-xl hover:bg-cyan-500/10 transition"
          aria-label="Toggle menu"
        >
          <FiMenu className="text-xl" />
        </button>
      </div>

      <div className="flex items-center gap-2 sm:gap-3">
        <button
          onClick={() => window.location.reload()}
          className="relative overflow-hidden flex items-center gap-2 px-3 sm:px-4 py-2 rounded-xl transition-all duration-300 text-sm font-medium text-cyan-200"
          style={{
            background: "rgba(6, 182, 212, 0.1)",
            border: "1px solid rgba(56, 189, 248, 0.25)",
            backdropFilter: "blur(8px)",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "rgba(6, 182, 212, 0.22)";
            e.currentTarget.style.borderColor = "rgba(56, 189, 248, 0.5)";
            e.currentTarget.style.transform = "translateY(-1px)";
            e.currentTarget.style.boxShadow = "0 8px 20px rgba(6, 182, 212, 0.25)";
            e.currentTarget.style.color = "#ffffff";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "rgba(6, 182, 212, 0.1)";
            e.currentTarget.style.borderColor = "rgba(56, 189, 248, 0.25)";
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = "none";
            e.currentTarget.style.color = "#cffafe";
          }}
          title="Refresh Page"
        >
          <FiRefreshCw className="text-base text-cyan-400" />
          <span className="hidden sm:inline">Refresh</span>
        </button>

        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setUserDropdownOpen(!userDropdownOpen)}
            className="relative overflow-hidden flex items-center gap-2 sm:gap-3 px-2 sm:px-3 py-1.5 rounded-xl transition-all duration-300"
            style={{
              background: "rgba(6, 182, 212, 0.1)",
              border: "1px solid rgba(56, 189, 248, 0.25)",
              backdropFilter: "blur(8px)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "rgba(6, 182, 212, 0.22)";
              e.currentTarget.style.borderColor = "rgba(56, 189, 248, 0.5)";
              e.currentTarget.style.transform = "translateY(-1px)";
              e.currentTarget.style.boxShadow = "0 8px 20px rgba(6, 182, 212, 0.25)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "rgba(6, 182, 212, 0.1)";
              e.currentTarget.style.borderColor = "rgba(56, 189, 248, 0.25)";
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "none";
            }}
          >
            <div
              className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
              style={{
                background: "linear-gradient(135deg, #06b6d4, #2563eb)",
                boxShadow: "inset 0 1px 1px rgba(255,255,255,0.4), 0 3px 10px rgba(6,182,212,0.4)",
              }}
            >
              <FiUser className="text-white text-base" />
            </div>
            <div className="text-left hidden md:block leading-tight">
              <p className="text-sm font-semibold text-white">{userName}</p>
              <p className="text-[11px] capitalize text-cyan-300">{userRole}</p>
            </div>
            <FiChevronDown
              className={`transition-transform duration-200 hidden sm:block text-cyan-300 ${userDropdownOpen ? "rotate-180" : ""}`}
            />
          </button>

          {userDropdownOpen && (
            <div
              className="absolute right-0 mt-2 w-72 rounded-2xl z-50 overflow-hidden"
              style={{
                background: "rgba(4, 18, 48, 0.92)",
                backdropFilter: "blur(20px)",
                border: "1px solid rgba(56, 189, 248, 0.35)",
                boxShadow: "0 24px 60px rgba(0, 3, 15, 0.8), 0 0 20px rgba(6,182,212,0.15)",
                animation: "modalIn 0.18s cubic-bezier(0.16,1,0.3,1)",
              }}
            >
              <div className="px-5 py-4 border-b border-cyan-500/15 bg-cyan-950/40">
                <div className="flex items-center gap-3">
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{
                      background: "linear-gradient(135deg, #06b6d4, #2563eb)",
                      boxShadow: "inset 0 1px 1px rgba(255,255,255,0.4), 0 4px 12px rgba(6,182,212,0.4)",
                    }}
                  >
                    <FiUser className="text-white text-xl" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold truncate text-white">{userName}</p>
                    <p className="text-xs truncate text-cyan-200/60">{userEmail}</p>
                    <span className="inline-block text-[10px] uppercase font-bold mt-1 px-2 py-0.5 rounded-full bg-cyan-500/20 text-cyan-300 border border-cyan-400/30">
                      Role: {userRole}
                    </span>
                  </div>
                </div>
              </div>

              <div className="py-2">
                {isSuperAdmin && (
                  <>
                    <Link
                      to="/admin-managment"
                      onClick={() => setUserDropdownOpen(false)}
                      className="flex items-center gap-3 px-5 py-2.5 text-sm transition-all text-cyan-100 hover:bg-cyan-500/15 hover:text-white"
                      style={
                        location.pathname === "/admin-managment"
                          ? { background: "linear-gradient(135deg, rgba(6,182,212,0.35), rgba(37,99,235,0.35))", color: "#ffffff", borderLeft: "3px solid #22d3ee" }
                          : {}
                      }
                    >
                      <MdAdminPanelSettings className="text-lg text-cyan-400" />
                      <span>Employee Management</span>
                    </Link>

                    <Link
                      to="/settings"
                      onClick={() => setUserDropdownOpen(false)}
                      className="flex items-center gap-3 px-5 py-2.5 text-sm transition-all text-cyan-100 hover:bg-cyan-500/15 hover:text-white"
                      style={
                        location.pathname === "/settings"
                          ? { background: "linear-gradient(135deg, rgba(6,182,212,0.35), rgba(37,99,235,0.35))", color: "#ffffff", borderLeft: "3px solid #22d3ee" }
                          : {}
                      }
                    >
                      <FiSettings className="text-lg text-cyan-400" />
                      <span>Settings</span>
                    </Link>
                  </>
                )}

                <button
                  onClick={() => {
                    setShowPasswordModal(true);
                    setUserDropdownOpen(false);
                  }}
                  className="w-full flex items-center gap-3 px-5 py-2.5 text-sm transition-all text-left text-cyan-100 hover:bg-cyan-500/15 hover:text-white"
                >
                  <FiLock className="text-lg text-cyan-400" />
                  <span>Password Update</span>
                </button>

                <div className="my-1 mx-4 border-t border-cyan-500/15" />

                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-5 py-2.5 text-sm transition-all text-left text-rose-300 hover:bg-rose-500/15"
                >
                  <FiLogOut className="text-lg text-rose-400" />
                  <span>Logout</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Password Update Modal */}
      {showPasswordModal && (
        <>
          <div
            className="fixed inset-0 z-[100]"
            style={{ background: "rgba(2, 7, 24, 0.8)", backdropFilter: "blur(10px)" }}
            onClick={closePasswordModal}
          />
          <div className="fixed inset-0 flex justify-center items-center z-[100] p-4">
            <div
              className="w-full max-w-md rounded-2xl overflow-hidden backdrop-blur-2xl"
              style={{
                background: "rgba(4, 18, 48, 0.92)",
                border: "1px solid rgba(56, 189, 248, 0.35)",
                boxShadow: "0 25px 70px rgba(0, 3, 15, 0.85), 0 0 25px rgba(6,182,212,0.2)",
              }}
            >
              <div className="flex items-center justify-between px-6 py-4 border-b border-cyan-500/20 bg-cyan-950/30">
                <h3 className="text-lg font-bold text-white m-0">Change Password</h3>
                <button 
                  onClick={closePasswordModal} 
                  className="text-cyan-300 hover:text-white p-1.5 rounded-lg hover:bg-cyan-500/20 transition"
                  aria-label="Close"
                >
                  <FiX size={18} />
                </button>
              </div>

              <form onSubmit={handlePasswordSubmit} className="space-y-4 px-6 py-5">
                {/* CURRENT PASSWORD */}
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-cyan-200 mb-1.5">
                    Current Password
                  </label>
                  <div className="relative">
                    <input
                      type={showCurrent ? "text" : "password"}
                      name="currentPassword"
                      value={passwordData.currentPassword}
                      onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                      required
                      className="w-full bg-cyan-950/60 text-cyan-50 text-sm rounded-xl px-4 py-2.5 pr-10 border border-cyan-400/30 focus:outline-none focus:border-cyan-300 backdrop-blur-md shadow-inner transition"
                    />
                    <span
                      onClick={() => setShowCurrent(!showCurrent)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer text-cyan-400 hover:text-cyan-200 transition"
                    >
                      {showCurrent ? <BsUnlockFill className="text-lg" /> : <IoMdLock className="text-lg" />}
                    </span>
                  </div>
                </div>

                {/* NEW PASSWORD */}
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-cyan-200 mb-1.5">
                    New Password
                  </label>
                  <div className="relative">
                    <input
                      type={showNew ? "text" : "password"}
                      name="newPassword"
                      value={passwordData.newPassword}
                      onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                      required
                      className="w-full bg-cyan-950/60 text-cyan-50 text-sm rounded-xl px-4 py-2.5 pr-10 border border-cyan-400/30 focus:outline-none focus:border-cyan-300 backdrop-blur-md shadow-inner transition"
                    />
                    <span
                      onClick={() => setShowNew(!showNew)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer text-cyan-400 hover:text-cyan-200 transition"
                    >
                      {showNew ? <BsUnlockFill className="text-lg" /> : <IoMdLock className="text-lg" />}
                    </span>
                  </div>
                </div>

                {/* CONFIRM PASSWORD */}
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-cyan-200 mb-1.5">
                    Confirm New Password
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirm ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      className="w-full bg-cyan-950/60 text-cyan-50 text-sm rounded-xl px-4 py-2.5 pr-10 border border-cyan-400/30 focus:outline-none focus:border-cyan-300 backdrop-blur-md shadow-inner transition"
                    />
                    <span
                      onClick={() => setShowConfirm(!showConfirm)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer text-cyan-400 hover:text-cyan-200 transition"
                    >
                      {showConfirm ? <BsUnlockFill className="text-lg" /> : <IoMdLock className="text-lg" />}
                    </span>
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-3">
                  <button
                    type="button"
                    onClick={closePasswordModal}
                    className="px-4 py-2 rounded-xl text-sm font-semibold bg-cyan-950/50 text-cyan-200 border border-cyan-500/30 hover:bg-cyan-900/50 transition"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-5 py-2 rounded-xl text-sm font-semibold text-white transition shadow-lg"
                    style={{
                      background: "linear-gradient(135deg, #06b6d4 0%, #2563eb 100%)",
                      boxShadow: "0 4px 15px rgba(6, 182, 212, 0.4)",
                    }}
                  >
                    Update Password
                  </button>
                </div>
              </form>
            </div>
          </div>
        </>
      )}
    </header>
  );
};

export default Header;
